/**
 * 認証保護ルート
 * 
 * @file ProtectedRoute.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * ロールベースでのアクセス制御を行うルートコンポーネント。
 * 管理者、販売者、購入者のロール判定とアクセス許可制御を実装。
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, useIsAdmin, useIsSeller, useIsCustomer } from '../contexts/useAuth';
import { AdminUser, UserRole } from '../types';
import Loading from './common/Loading';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole | UserRole[];
  adminOnly?: boolean;
  sellerOnly?: boolean;
  buyerOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  adminOnly = false,
  sellerOnly = false,
  buyerOnly = false
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const isSeller = useIsSeller();
  const isCustomer = useIsCustomer();
  const isBuyer = user?.role === UserRole.BUYER;  // Buyer判定追加

  // デバッグログ: ProtectedRoute認証チェック
  console.log('[ProtectedRoute]', {
    path: location.pathname,
    isLoading,
    isAuthenticated,
    user,
    adminOnly,
    sellerOnly
  });

  // ローディング中
  if (isLoading) {
    console.log('[ProtectedRoute] Loading... showing Loading component');
    return <Loading variant="page" />;
  }

  // 強制パスワード変更が必要な場合（/admin/force-password-change 以外は全てブロック）
  const adminUser = user as AdminUser | null;
  if (
    isAuthenticated &&
    adminUser?.forcePasswordChange === true &&
    location.pathname !== '/admin/force-password-change'
  ) {
    console.log('[ProtectedRoute] forcePasswordChange=true, redirecting to /admin/force-password-change');
    return <Navigate to="/admin/force-password-change" replace />;
  }

  // 強制変更不要なのに変更画面にいる場合はダッシュボードへ
  if (
    isAuthenticated &&
    adminUser?.forcePasswordChange === false &&
    location.pathname === '/admin/force-password-change'
  ) {
    console.log('[ProtectedRoute] forcePasswordChange=false, redirecting to /admin');
    return <Navigate to="/admin" replace />;
  }

  // 未認証の場合、ロール別ログインページにリダイレクト
  if (!isAuthenticated) {
    const pathname = location.pathname;
    let loginPath = '/login';

    // 現在のパスから適切なログインページを判定
    if (pathname.startsWith('/admin')) {
      loginPath = '/login';
    } else if (pathname.startsWith('/seller')) {
      loginPath = '/login';
    }

    console.log(`[ProtectedRoute] Not authenticated, redirecting to ${loginPath}`);
    // 元のパスをsessionStorageに保存（location.stateが失われる場合の代替手段）
    sessionStorage.setItem('redirectAfterLogin', pathname);
    // replaceをfalseにして履歴に追加（location.stateが適切に渡されるように）
    return <Navigate to={loginPath} state={{ from: location }} />;
  }

  // ロール別アクセス制御
  if (adminOnly && !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  if (sellerOnly && !isSeller) {
    return <Navigate to="/login" replace />;
  }

  if (buyerOnly && !isBuyer) {
    return <Navigate to="/login" replace />;
  }

  // 特定のロールが必要な場合
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!user || !roles.includes(user.role)) {
      // アクセス権限なし - 適切なページにリダイレクト
      if (isAdmin) return <Navigate to="/admin" replace />;
      if (isSeller) return <Navigate to="/seller" replace />;
      if (isCustomer) return <Navigate to="/" replace />;

      // ロールなしの場合は現在のパスから判定
      const pathname = location.pathname;
      if (pathname.startsWith('/admin')) return <Navigate to="/login" replace />;
      if (pathname.startsWith('/seller')) return <Navigate to="/login" replace />;
      return <Navigate to="/login" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
